import type { ComponentPropsWithoutRef, ElementRef } from 'react'
import { forwardRef, useMemo } from 'react'

import { TeamMemberRole } from '@api'
import * as SelectPrimitive from '@radix-ui/react-select'
import type { Row } from '@tanstack/react-table'
import { useUnit } from 'effector-react'

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectTrigger,
} from '@/components/ui/select.tsx'
import { $account } from '@/models/account.ts'
import type { Creator } from '@/modules/creators/types.ts'
import type { BillingPlan, License } from '@/modules/licenses/types.ts'
import { classNames } from '@/utils/helpers.ts'

interface PlanChangeInfo {
  isCurrentPlan: boolean
  availableLicenses: License[]
  availableLicensesCount: number
}

const planValues = (
  plan: BillingPlan,
  licenses: License[],
  currentPlan?: BillingPlan | null,
): PlanChangeInfo => {
  const isCurrentPlan = plan.name === currentPlan?.name
  const availableLicenses = licenses.filter(
    (license) =>
      !license.disabled &&
      license.plan.startsWith(plan.name) &&
      license.activeUsed < license.quantity,
  )
  const availableLicensesAmounts = availableLicenses.map(
    (license) => license.quantity - license.activeUsed,
  )
  const availableLicensesCount =
    availableLicensesAmounts.length > 0
      ? availableLicensesAmounts.reduce((a, b) => a + b)
      : 0

  return { isCurrentPlan, availableLicenses, availableLicensesCount }
}

export const LicenseChanger = ({
  row,
  plans,
  licenses,
  openPurchaseLicense,
  openSetLicense,
  openUpgradeLicense,
}: {
  row: Row<Creator>
  plans: BillingPlan[]
  licenses: License[]
  openPurchaseLicense: (creator: Creator, plan: BillingPlan) => void
  openSetLicense: (
    creator: Creator,
    license: License,
    plan: BillingPlan,
  ) => void
  openUpgradeLicense: (creator: Creator, plan: BillingPlan) => void
}) => {
  const account = useUnit($account)
  const currentPlan = useMemo(
    () =>
      row.original.plan != null
        ? plans.find((plan) => row.original.plan.startsWith(plan.name))
        : null,
    [plans, row],
  )
  const currentPlanLevel = useMemo(
    () => (currentPlan != null ? currentPlan.level : -1),
    [currentPlan],
  )
  const availablePlans = useMemo(
    () =>
      plans.filter(
        (plan) => currentPlanLevel != null && currentPlanLevel < plan.level,
      ),
    [currentPlanLevel, plans],
  )

  if (currentPlan == null && row.original.plan != null) {
    return row.original.plan
  }

  if (!account || account.memberRole !== TeamMemberRole.LEADER) {
    return currentPlan ? currentPlan.displayName : 'No license'
  }

  const badgeClasses =
    'inline-flex items-center rounded-full px-3 font-normal bg-primary-500/10 text-primary-500 h-6 text-[0.7rem]'

  if (
    currentPlan?.name === 'professional' &&
    row.original.plan != null &&
    row.original.plan.endsWith('_monthly')
  ) {
    return (
      <Select
        value="professional_monthly"
        onValueChange={(value) => {
          const plan = plans.find((plan) => plan.name === 'professional')

          if (plan) {
            openUpgradeLicense(row.original, plan)
          }
        }}
      >
        <SelectTrigger>
          <div className="flex">Professional</div>
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <LicenseSelectItem value="professional_yearly">
              <div className="flex items-center justify-between gap-2">
                <SelectPrimitive.ItemText className="font-medium">
                  Professional
                </SelectPrimitive.ItemText>
                <div className={badgeClasses}>Yearly</div>
              </div>
            </LicenseSelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
    )
  }

  const onPlanChange = (value: string) => {
    if (value === 'none') {
      return
    }

    const plan = availablePlans.find((plan) => plan.name === value)

    if (!plan) {
      return
    }

    const { isCurrentPlan, availableLicenses, availableLicensesCount } =
      planValues(plan, licenses, currentPlan)

    if (isCurrentPlan) {
      return
    }

    if (
      !availableLicenses ||
      availableLicenses.length === 0 ||
      availableLicensesCount === 0
    ) {
      if (currentPlan == null) {
        openPurchaseLicense(row.original, plan)

        return
      }

      openUpgradeLicense(row.original, plan)

      return
    }

    const targetLicense = availableLicenses[0]

    openSetLicense(row.original, targetLicense, plan)
  }

  return (
    <Select
      value={currentPlan?.name ?? 'none'}
      onValueChange={onPlanChange}
      disabled={currentPlan?.name === 'professional'}
    >
      <SelectTrigger>
        {currentPlan == null ? (
          <div className="flex">Select a License</div>
        ) : (
          <div className="flex">{currentPlan.displayName}</div>
        )}
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {availablePlans.map((plan) => {
            const { isCurrentPlan, availableLicensesCount } = planValues(
              plan,
              licenses,
              currentPlan,
            )

            return (
              <LicenseSelectItem
                key={plan.name}
                value={plan.name}
                className="whitespace-normal"
              >
                <div className="flex w-full flex-row items-center justify-between gap-2">
                  <SelectPrimitive.ItemText className="font-medium">
                    {plan.displayName}
                  </SelectPrimitive.ItemText>
                  {!isCurrentPlan &&
                    availableLicensesCount === 0 &&
                    currentPlan == null && (
                      <div className={badgeClasses}>Get now</div>
                    )}
                  {!isCurrentPlan &&
                    availableLicensesCount === 0 &&
                    currentPlan != null && (
                      <div className={badgeClasses}>Upgrade</div>
                    )}
                  {!isCurrentPlan && availableLicensesCount > 0 && (
                    <p className="text-[0.7rem] font-normal">
                      Available {availableLicensesCount}x
                    </p>
                  )}
                </div>
              </LicenseSelectItem>
            )
          })}
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}

// eslint-disable-next-line react/display-name
const LicenseSelectItem = forwardRef<
  ElementRef<typeof SelectPrimitive.Item>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={classNames(
      "hover:bg-secondary-50 hover:dark:bg-secondary-600 relative flex-1 cursor-pointer select-none items-center rounded-sm px-3 py-1.5 text-sm outline-none data-[disabled='true']:pointer-events-none data-[disabled='true']:opacity-50 dark:text-white",
      className,
    )}
    {...props}
  >
    {children}
  </SelectPrimitive.Item>
))
